import React from "react";
import { FiUpload, FiTrash2, FiDownload } from "react-icons/fi";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { AiFillFilePdf } from "react-icons/ai";

const FileUploadInput = ({
  fileInputRef,
  onFileChange,
  files,
  onRemoveFile,
  existingsFiles,
  onValidationError,
}) => {
  const isImage = (file) => file?.type?.startsWith("image/");
  const isPDF = (file) => file?.type === "application/pdf";

  const isPdfUrl = (url) => {
    try {
      const path = new URL(url).pathname.toLowerCase();
      return /\.pdf(\?|$)/.test(path);
    } catch {
      return false;
    }
  };
  const isImageUrl = (url) => {
    try {
      const path = new URL(url).pathname.toLowerCase();
      return /\.(png|jpe?g)(\?|$)/.test(path);
    } catch {
      return false;
    }
  };

  // File validation function
  const isValidFileType = (file) => {
    const allowedTypes = [
      "image/png",
      "image/jpg",
      "image/jpeg",
      "application/pdf",
    ];
    return allowedTypes.includes((file?.type || "").toLowerCase());
  };

  const isValidFileSize = (file, maxSizeMB = 5) =>
    (file?.size || 0) <= maxSizeMB * 1024 * 1024; // Convert MB to bytes

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const validFiles = [];
    const invalidFiles = [];

    selectedFiles.forEach((file) => {
      // Check file type
      if (!isValidFileType(file)) {
        invalidFiles.push({
          name: file.name,
          reason: `Unsupported file type. Only PNG, JPG, JPEG, and PDF files are allowed.`,
        });
        return;
      }

      // Check file size
      if (!isValidFileSize(file)) {
        invalidFiles.push({
          name: file.name,
          reason: `File size too large. Maximum size is 5MB.`,
        });
        return;
      }

      validFiles.push(file);
    });

    // Show validation errors if any
    if (invalidFiles.length > 0 && onValidationError) {
      onValidationError(invalidFiles);
    }

    // Only pass valid files to parent component
    if (validFiles.length > 0) {
      // Create a synthetic event object for the valid files
      onFileChange({
        target: {
          files: validFiles,
        },
      },
    );
    }
  };

  const getFilenameFromUrl = (url, fallback = "document") => {
    try {
      const u = new URL(url);
      const last = u.pathname.split("/").filter(Boolean).pop() || fallback;
      return last.split("?")[0].split("#")[0] || fallback;
    } catch {
      return fallback;
    }
  };

  const downloadBlob = (blob, filename) => {
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 500);
  };

  const guessExtension = (mime) => {
    if (mime === "application/pdf") return ".pdf";
    if (mime === "image/png") return ".png";
    if (mime === "image/jpeg" || mime === "image/jpg") return ".jpg";
    return "";
  };

  const handleDownload = async (file, index, isExisting = false) => {
    try {
      if (isExisting) {
        const url = file;
        const res = await fetch(url, { mode: "cors" });
        if (!res.ok) throw new Error("Failed to fetch file for download");
        const blob = await res.blob();
        const ext =
          guessExtension(blob.type) ||
          (() => {
            const name = getFilenameFromUrl(url);
            const dot = name.lastIndexOf(".");
            return dot !== -1 ? name.slice(dot) : "";
          })();
        const base = getFilenameFromUrl(url, `document-${index}`);
        const filename = base.includes(".") ? base : `${base}${ext}`;
        downloadBlob(blob, filename);
      } else {
        const blob = file;
        const filename =
          file.name?.trim()?.length > 0
            ? file.name
            : `document-${index}${guessExtension(file.type)}`;
        downloadBlob(blob, filename);
      }
    } catch (err) {
      // Fallback: open in new tab if something blocks download (CORS, headers)
      if (isExisting && typeof file === "string") {
        window.open(file, "_blank", "noopener,noreferrer");
      } else {
        console.error("Download failed:", err);
      }
    }
  };

  const PdfTile = ({ label = "PDF", onOpen }) => (
    <div
      className="flex flex-col items-center justify-center w-full h-full bg-white"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onOpen?.()}
    >
      <AiFillFilePdf className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 text-red-600" />
      <span className="mt-2 text-xs sm:text-sm text-gray-700 px-2 text-center line-clamp-2">
       {/* {label}*/}PDF
      </span>
    </div>
  );

  return (
    <div>
      <label className="text-[#09114B] text-[11px] sm:text-[13px] md:text-[15px] lg:text-[17px] font-inter font-medium mb-2 block">
        Support Documents (Optional)
      </label>
      <div
        className="
          relative
          flex flex-wrap w-full md:h- lg:h-[234px] p-[8px] sm:p-[12px] md:p-[16px] lg:p-[20px] 
          gap-4 sm:gap-5 md:gap-6 lg:gap-7 justify-start

          rounded-[5px]
          border border-[rgba(129,129,129,0.5)]
          text-center
          bg-[#F7F9FF] 
          overflow-auto
        "
        //  onClick={() => fileInputRef.current.click()}
      >
        {files.length > 0 || existingsFiles.length > 0 ? (
          <>
            {/* Render existing files (URLs) first, if any */}
            {existingsFiles.map((urlfile, index) => {
              const name = getFilenameFromUrl(urlfile, `document-${index}`);
              const showImage = isImageUrl(urlfile);
              const showPdf = isPdfUrl(urlfile);

              return (
              <div
                key={`existing-${index}`}
                className="group relative w-[92px] h-[92px] sm:w-[122px] sm:h-[122px] md:w-[152px] md:h-[152px] lg:w-[192px] lg:h-[192px] rounded-[8px] overflow-hidden border border-gray-300 bg-gray-50 flex items-center justify-center"
              >
                  {showImage ? (
                <img
                  src={urlfile}
                  alt={name}
                  className="object-cover w-full h-full"
                      loading="lazy"
                    />
                  ) : showPdf ? (
                    <PdfTile
                      label={name}
                      onOpen={() =>
                        window.open(urlfile, "_blank", "noopener,noreferrer")
                      }
                    />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full text-sm text-gray-500">
                      File
                    </div>
                  )}

                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(urlfile, index, true);
                    }}
                    className="absolute top-[60px] sm:top-[90px] md:top-[112px] lg:top-[145px] lg:right-20 p-1 bg-white rounded-[6px] shadow hover:bg-red-50"
                    title="Download"
                    type="button"
                  >
                    <FiDownload className="text-[#3B82F6] w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6" />
                  </button>
                </div>
              </div>
            );
            })}

            {/* Render newly uploaded files */}
            {files.map((file, index) => (
              <div
                key={`new-${index}`}
                className="group relative w-[92px] h-[92px] sm:w-[122px] sm:h-[122px] md:w-[152px] md:h-[152px] lg:w-[192px] lg:h-[192px] rounded-[8px] overflow-hidden border border-gray-300 bg-gray-50 flex items-center justify-center"
              >
                {isImage(file) ? (
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name || "preview"}
                    className="object-cover w-full h-full"
                  />
                ) : isPDF(file) ? (
                  <PdfTile
                    label={file.name || "document.pdf"}
                    onOpen={() => {
                      const url = URL.createObjectURL(file);
                      window.open(url, "_blank", "noopener,noreferrer");
                      setTimeout(() => URL.revokeObjectURL(url), 30000);
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center w-full h-full text-sm text-gray-400">
                    File
                  </div>
                )}

                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(file, index);
                    }}
                    className="absolute top-[60px] sm:top-[90px] md:top-[112px] lg:top-[145px] lg:right-20 p-1 bg-white rounded-[6px] shadow hover:bg-red-50"
                    title="Download"
                    type="button"
                  >
                    <FiDownload className="text-[#3B82F6] w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6" />
                  </button>
                </div>
              </div>
            ))}
          </>
        ) : (
          <div
            className="flex flex-col justify-center items-center gap-[14px] w-full font-[400] text-center text-gray-500"
          >
          </div>
        )}
      </div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        multiple
        accept=".png,.jpg,.jpeg,.pdf"
        className="hidden"
      />
    </div>
  );
};

export default FileUploadInput;
